import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_TTS,
  getLanguageGroup,
  getLanguageOptions,
  getVoicesByLanguage,
  TwilioVoiceCatalogEntry,
} from "../constants/twilio-voice-catalog";
import { voiceMessagesFieldSx } from "../constants/voice-messages-field-sx";

const ORANGE = "#FB8E00";

const fieldSx = voiceMessagesFieldSx;

export type TtsSelection = {
  language: string;
  voice: string;
};

type Props = {
  value: TtsSelection;
  onChange: (value: TtsSelection) => void;
};

export function TwilioVoiceSelector({ value, onChange }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [loadingVoice, setLoadingVoice] = useState<string | null>(null);

  const languageOptions = useMemo(() => getLanguageOptions(), []);
  const selectedLanguageGroup = useMemo(() => getLanguageGroup(value.language), [value.language]);
  const voices = useMemo(() => getVoicesByLanguage(selectedLanguageGroup), [selectedLanguageGroup]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleLanguageChange = (languageGroup: string) => {
    const nextVoices = getVoicesByLanguage(languageGroup);
    const nextVoice = nextVoices[0];

    if (!nextVoice) {
      onChange({ language: DEFAULT_TTS.language, voice: DEFAULT_TTS.voice });
      return;
    }

    onChange({ language: nextVoice.language, voice: nextVoice.voice });
  };

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoice(null);
  };

  const handlePreview = async (entry: TwilioVoiceCatalogEntry) => {
    if (playingVoice === entry.voice) {
      stopPreview();
      return;
    }

    stopPreview();
    setLoadingVoice(entry.voice);

    try {
      const audio = new Audio(entry.previewUrl);
      audioRef.current = audio;

      audio.onended = () => stopPreview();
      audio.onerror = () => stopPreview();

      setPlayingVoice(entry.voice);
      await audio.play();
    } catch (error) {
      console.error(error);
      stopPreview();
    } finally {
      setLoadingVoice(null);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.5 }}>
        Voice &amp; language
      </Typography>

      <Typography sx={{ fontSize: 13, color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500, mb: 1.5, lineHeight: 1.4 }}>
        This voice and language apply to all auto-attendant spoken messages (queue, recording notice, and voicemail).
      </Typography>

      <TextField
        select
        label="Language"
        value={selectedLanguageGroup}
        onChange={(event) => handleLanguageChange(event.target.value)}
        fullWidth
        sx={{ mb: 1.5, ...fieldSx }}
      >
        {languageOptions.map((option) => (
          <MenuItem key={option.language} value={option.language}>
            {option.languageLabel}
          </MenuItem>
        ))}
      </TextField>

      <Stack spacing={1}>
        {voices.map((entry) => {
          const isSelected = value.voice === entry.voice;
          const isPlaying = playingVoice === entry.voice;
          const isLoading = loadingVoice === entry.voice;

          return (
            <Box
              key={entry.voice}
              onClick={() => onChange({ language: entry.language, voice: entry.voice })}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1.5,
                py: 1.2,
                borderRadius: "10px",
                border: isSelected ? `2px solid ${ORANGE}` : "1px solid rgba(0,0,0,0.08)",
                bgcolor: isSelected ? "rgba(251,142,0,0.06)" : "var(--fx-surface-base, #FFFFFF)",
                cursor: "pointer",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                  {entry.label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                  {entry.languageLabel} · {entry.gender === "female" ? "Female" : "Male"}
                </Typography>
              </Box>

              <IconButton
                size="small"
                disabled={isLoading}
                onClick={(event) => {
                  event.stopPropagation();
                  void handlePreview(entry);
                }}
                sx={{
                  color: ORANGE,
                  border: "1px solid rgba(251,142,0,0.35)",
                }}
              >
                {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
